import * as sdk from "@kaggle/sdk";
import { listKernelVersionsResponse } from "./listKernelVersionsResponse";

const KERNEL_ID = 10;
const KERNEL_RUN_ID = 2001;
const KERNEL_VERSION_ID = 3000;
const USER_ID = 1000;

const getCreateKernelResponse = (id: number) => ({
  id,
  kernelRunId: KERNEL_RUN_ID,
  currentUrlSlug: "random url",
  kernelVersionId: KERNEL_VERSION_ID,
});

/** Mocked implementation of kernelsClient. */
export const kernelsClient: typeof sdk.kernelsClient = {
  ...sdk.kernelsClient,
  autoSave: sdk.unboxSdk(sdk.kernelsClient.autoSave).mock(async req => {
    await sdk.delay(1000);
    return {
      sequence: req.sequence + 1,
      kernelVersionId: KERNEL_VERSION_ID,
      computedParent: null,
    };
  }),
  cancelKernelRun: sdk
    .unboxSdk(sdk.kernelsClient.cancelKernelRun)
    .mock(async _req => ({})),
  createKernel: sdk.unboxSdk(sdk.kernelsClient.createKernel).mock(async req => {
    return getCreateKernelResponse(req.forkedFromRunId || KERNEL_ID);
  }),
  createKernelRun: sdk
    .unboxSdk(sdk.kernelsClient.createKernelRun)
    .mock(async _req => {
      await sdk.delay(1000);
      return {
        id: KERNEL_RUN_ID,
        resources: {
          storageBytes: 5 * 1024 * 1024 * 1024,
          memoryBytes: 16 * 1024 * 1024 * 1024,
          cpuCores: 4,
          sessionTimeoutSeconds: 9 * 60 * 60,
          timeSeconds: 9 * 60 * 60,
          idleTimeoutSeconds: 1 * 60 * 60,
          network: "no-external",
        },
        error: null,
        useNewKernelsBackend: false,
        showParentForkSecretWarning: false,
      };
    }),
  deleteKernel: sdk
    .unboxSdk(sdk.kernelsClient.deleteKernel)
    .mock(async _req => {
      throw new Error("Not implemented.");
    }),
  getKernel: sdk.unboxSdk(sdk.kernelsClient.getKernel).mock(async req => {
    await sdk.delay(100);
    return {
      id: req.kernelId ? req.kernelId : KERNEL_ID,
      author: {} as any,
      bestPublicScore: null,
      commentCount: 0,
      currentRunId: KERNEL_RUN_ID,
      forkCount: 0,
      forkParent: {} as any,
      isLanguageTemplate: false,
      isPrivate: true,
      medal: undefined,
      mostRecentRunId: KERNEL_RUN_ID,
      pinnedDockerImageVersionId: 0,
      readGroupId: 0,
      selfLink: "",
      slug: "",
      tags: [],
      title: "Fake Title",
      topicId: 0,
      updatedTime: "2018-08-05T20:19:50.905Z",
      upvoteCount: 0,
      url: "willbrazil/abc-random-url",
      viewCount: 0,
      writeGroupId: 0,
      // HACK(cochamberlain): GET COMPILING
    } as any;
  }),
  getKernelRun: sdk.unboxSdk(sdk.kernelsClient.getKernelRun).mock(async req => {
    await sdk.delay(500);
    return {
      id: req.kernelRunId ? req.kernelRunId : KERNEL_RUN_ID,
      kernelId: KERNEL_ID,
      commit: {} as any,
      dateCreated: "2018-08-05T20:19:50.905Z",
      dateEvaluated: "2018-08-05T20:19:50.905Z",
      isolatorResults: "",
      language: 8 as any,
      outputFilesTotalSizeBytes: 10,
      renderedOutputUrl: "",
      runInfo: {} as any,
      scriptLanguageId: 8 as any,
      scriptLanguageName: "",
      sourceType: "notebook",
      status: "complete" as any,
      title: "Fake Title 2",
      type: null as any,
      workerContainerPort: null,
      workerIPAddress: null as any,
      workerUptimeSeconds: null as any,
      // HACK(cochamberlain): GET COMPILING
    } as any;
  }),
  listKernelVersions: sdk
    .unboxSdk(sdk.kernelsClient.listKernelVersions)
    .mock(async _req => {
      return listKernelVersionsResponse;
    }),
  listKernelIds: sdk
    .unboxSdk(sdk.kernelsClient.listKernelIds)
    .mock(async _req => {
      return [1];
    }),
  listKernelRunLogs: sdk
    .unboxSdk(sdk.kernelsClient.listKernelRunLogs)
    .mock(async _req => ({
      logs: [
        {
          content:
            "[NbConvertApp] Converting notebook __notebook__.ipynb to notebook↵",
          elapsedSeconds: 2.661409623993677,
          lineNumber: 1,
          streamName: "stdout",
        },
      ],
    })),
  getKernelListDetails: sdk
    .unboxSdk(sdk.kernelsClient.getKernelListDetails)
    .mock(async _req => {
      return {
        kernels: [
          {
            id: 1,
            scriptUrl: "will/brazil",
            title: "Map of Restaurants in San Francisco",
            dataSources: [
              {
                sourceId: 10,
                name: "FIFA",
                children: [
                  {
                    id: 100,
                    name: "Something.csv",
                  },
                ],
              },
            ],
          },
          {
            id: 2,
            scriptUrl: "will/brazil2",
            title: "Feature Extraction Scaling",
          },
        ],
        totalCount: 2,
      } as any;
    }),
  promoteKernelDraft: sdk
    .unboxSdk(sdk.kernelsClient.promoteKernelDraft)
    .mock(async _req => {
      throw new Error("Not implemented.");
    }),
  commitAndRun: sdk
    .unboxSdk(sdk.kernelsClient.commitAndRun)
    .mock(async _req => {
      await sdk.delay(300);
      return {
        // On commit and run the backend resets sequence to 0.
        sequence: 0,
        kernelRunId: KERNEL_RUN_ID + 1,
        kernelVersionId: KERNEL_VERSION_ID + 1,
      } as any;
    }),
  listDockerImages: sdk
    .unboxSdk(sdk.kernelsClient.listDockerImages)
    .mock(async _req => ({
      images: [],
      selectedVersionType: "original",
      selectedVersionId: null,
    })),
  getKernelCollaboratorList: sdk
    .unboxSdk(sdk.kernelsClient.getKernelCollaboratorList)
    .mock(async _req => ({
      collaboratorList: {
        collaborators: [
          {
            groupId: null,
            groupMemberCount: null,
            isGroup: false,
            isUser: true,
            joinDate: null,
            name: "Cole",
            profileUrl: "/noderaider",
            slug: "noderaider",
            thumbnailUrl:
              "https://storage.googleapis.com/kaggle-avatars/thumbnails/836731-kg.jpg",
            type: "writer",
            userId: 836731,
            userTier: null,
          },
        ],
        owner: {
          groupId: null,
          groupMemberCount: null,
          isGroup: false,
          isUser: true,
          joinDate: null,
          name: "Will Guedes",
          profileUrl: "/willbrazil",
          slug: "willbrazil",
          thumbnailUrl:
            "https://storage.googleapis.com/kaggle-avatars/thumbnails/2055066-kg.jpg",
          type: "owner",
          userId: 2055066,
          userTier: 5,
        },
      },
      errorMessage: "",
    })),
};

export const datasetsClient: typeof sdk.datasetsClient = {
  ...sdk.datasetsClient,
  searchDatasets: sdk
    .unboxSdk(sdk.datasetsClient.searchDatasets)
    .mock(async _req => {
      return {
        datasetList: {
          totalResults: 10,
          // tslint:disable-next-line
          items: require("./searchDatasourcesResponse.json") as any,
        },
        hasMore: false,
        topicalDataset: null,
        includeTopicalDatasets: false,
      };
    }),
};

export const usersClient: typeof sdk.usersClient = {
  ...sdk.usersClient,
  getCurrentUserId: sdk
    .unboxSdk(sdk.usersClient.getCurrentUserId)
    .mock(async _req => {
      await sdk.delay(100);
      return USER_ID;
    }),
  getCurrentUser: sdk
    .unboxSdk(sdk.usersClient.getCurrentUser)
    .mock(async _req => {
      await sdk.delay(100);
      return {
        id: USER_ID,
        userName: "chuck_user",
        displayName: "Chuck",
        isAdmin: true,
        isPhoneVerified: true,
        // tslint:disable-next-line:max-line-length
        thumbnailUrl:
          "https://static-cdn.jtvnw.net/jtv_user_pictures/hsdogdog-profile_image-5550ade194780dfc-300x300.jpeg",
      } as any;
    }),
};

export const educationClient: typeof sdk.educationClient = {
  ...sdk.educationClient,
};
